import React, { useMemo, useState } from 'react';
import { ArrowUpRightIcon, CalculatorIcon, TriangleAlertIcon } from 'lucide-react';
import { Card, CardHeader } from './Card';
import { TierLadderTable } from './TierLadderTable';
import { PAR_PRICE } from '../data/commissionData';
import type { LoanOfficer, TierPlan } from '../types/commission';
import { buildLoanScenarioRows, calcOverageDollars, findTierByUnits, getNextTier } from '../utils/commission';
import { formatBps, formatCurrency, formatPercent, formatPrice, parseNumericInput } from '../utils/format';
interface LoanScenarioToolProps {
  officer: LoanOfficer;
  plan: TierPlan;
  /** Units already closed, used to decide which tier this loan lands in */
  unitsBefore: number;
}
export function LoanScenarioTool({
  officer,
  plan,
  unitsBefore
}: LoanScenarioToolProps) {
  const [loanAmountInput, setLoanAmountInput] = useState('325,000');
  const [sellPriceInput, setSellPriceInput] = useState('101.250');
  const loanAmount = parseNumericInput(loanAmountInput);
  const sellPrice = parseNumericInput(sellPriceInput);
  const rows = useMemo(() => buildLoanScenarioRows({
    plan,
    unitsBefore,
    loanAmount,
    sellPrice
  }), [plan, unitsBefore, loanAmount, sellPrice]);
  const unitsAfter = Math.floor(unitsBefore) + 1;
  const tierBefore = findTierByUnits(plan, unitsBefore);
  const landingTier = findTierByUnits(plan, unitsAfter);
  const landingRow = rows.find(row => row.status === 'current') ?? rows[0];
  const nextTier = getNextTier(plan, landingTier);
  const nextRow = nextTier ? rows.find(row => row.tier.id === nextTier.id) : undefined;
  const overageDollars = calcOverageDollars(loanAmount, sellPrice);
  const pushesTier = landingTier.id !== tierBefore.id;
  const invalid = loanAmount <= 0;
  return <Card data-id="element-36" data-mp="1xnz9gshesx0j">
      <CardHeader title="Single loan payout" description="Enter a loan amount and what it sells for to see the payout at each tier." icon={<CalculatorIcon className="h-4 w-4" aria-hidden="true" data-id="element-38" data-mp="27ku615u4gnc6" />} data-id="element-37" data-mp="22l95iqzb06xc" />
      <div className="grid gap-5 px-5 py-5 lg:grid-cols-[300px_minmax(0,1fr)]" data-id="element-39" data-mp="7hb4zcyr4cw3">
        <div className="space-y-4" data-id="element-40" data-mp="2ji3gx8e8725">
          <div data-id="element-41" data-mp="2kb3zggre0bu">
            <label htmlFor="loan-amount" className="block text-xs font-semibold uppercase tracking-wide text-slate-500" data-id="element-42" data-mp="2kt5aria34qm">
              Loan amount
            </label>
            <div className="relative mt-1.5" data-id="element-43" data-mp="cf2z8dmvcb3g">
              <span className="pointer-events-none absolute inset-y-0 left-3 flex items-center text-sm text-slate-500" data-id="element-44" data-mp="q7sw5d3xr0fc">
                $
              </span>
              <input id="loan-amount" inputMode="decimal" value={loanAmountInput} onChange={event => setLoanAmountInput(event.target.value)} aria-describedby={invalid ? 'loan-amount-error' : undefined} className="w-full rounded-lg border border-slate-300 py-2 pl-7 pr-3 text-sm font-medium tabular-nums shadow-sm focus:border-brand-500 focus:outline-none focus:ring-2 focus:ring-brand-200" data-id="element-45" data-mp="m9bngp4v9hvt" />
            </div>
            {invalid ? <p id="loan-amount-error" className="mt-1.5 text-xs font-medium text-red-600" data-id="element-46" data-mp="1hf15rn9dqnvk">
                Enter a loan amount greater than $0.
              </p> : null}
          </div>

          <div data-id="element-47" data-mp="17p7xav4jaovj">
            <label htmlFor="sell-price" className="block text-xs font-semibold uppercase tracking-wide text-slate-500" data-id="element-48" data-mp="17oooc3wv3atn">
              Selling the deal for
            </label>
            <input id="sell-price" inputMode="decimal" value={sellPriceInput} onChange={event => setSellPriceInput(event.target.value)} className="mt-1.5 w-full rounded-lg border border-slate-300 px-3 py-2 text-sm font-medium tabular-nums shadow-sm focus:border-brand-500 focus:outline-none focus:ring-2 focus:ring-brand-200" data-id="element-49" data-mp="yqw5wqvufn21" />
            <p className="mt-1.5 text-xs text-slate-500" data-id="element-50" data-mp="2j329v2457e6i">
              Par is {formatPrice(PAR_PRICE)}. Above par creates{' '}
              {formatCurrency(Math.max(0, overageDollars))} of overage on this loan.
            </p>
          </div>

          <div className="rounded-lg bg-slate-50 px-3 py-3 text-xs text-slate-600" data-id="element-51" data-mp="a1pbbuz9u41o">
            <p data-id="element-52" data-mp="53xcsi44fjoi">
              This would be {officer.name.split(' ')[0]}&rsquo;s loan #
              <strong className="font-semibold text-slate-800" data-id="element-53" data-mp="42lwekciu3g">{unitsAfter}</strong> this month,
              landing in{' '}
              <strong className="font-semibold text-slate-800" data-id="element-54" data-mp="3rhxizpt8zou5">
                {landingTier.name} ({formatBps(landingTier.basisPoints)},{' '}
                {formatPercent(landingTier.overageSplit)} overage)
              </strong>
              .
            </p>
          </div>

          <div className="rounded-xl border border-brand-200 bg-brand-50 px-4 py-3.5" data-id="element-55" data-mp="3djqswvujit51">
            <p className="text-xs font-semibold uppercase tracking-wide text-brand-700" data-id="element-56" data-mp="38ke62b3o5m2g">
              Payout on this loan
            </p>
            <p className="mt-1 text-3xl font-semibold tabular-nums text-brand-800" data-id="element-57" data-mp="2yfe8a10jhgkm">
              {formatCurrency(landingRow.total)}
            </p>
            <dl className="mt-2.5 space-y-1 text-xs text-brand-900" data-id="element-58" data-mp="38edkftkijucd">
              <div className="flex justify-between" data-id="element-59" data-mp="38f53k4wxbzlr">
                <dt data-id="element-60" data-mp="3ddzw3ykp12j2">Loan pay ({formatBps(landingTier.basisPoints)})</dt>
                <dd className="font-semibold tabular-nums" data-id="element-61" data-mp="2fpptbhd32aqw">{formatCurrency(landingRow.basePay)}</dd>
              </div>
              <div className="flex justify-between" data-id="element-62" data-mp="2ko9gtm7bruld">
                <dt data-id="element-63" data-mp="2kp2dtvghf5qb">Unit bonus</dt>
                <dd className="font-semibold tabular-nums" data-id="element-64" data-mp="2po5l7mjxpo1g">
                  {formatCurrency(landingRow.unitBonus)}
                </dd>
              </div>
              <div className="flex justify-between" data-id="element-65" data-mp="2ug5if78me2uc">
                <dt data-id="element-66" data-mp="1wzqa3b3xbpve">Overage share</dt>
                <dd className="font-semibold tabular-nums" data-id="element-67" data-mp="1wyowayviwp9z">
                  {formatCurrency(landingRow.overageShare)}
                </dd>
              </div>
            </dl>
          </div>
        </div>

        <div className="space-y-4" data-id="element-68" data-mp="1tw0sv3l5rkme">
          {pushesTier ? <div className="flex items-start gap-3 rounded-xl border border-emerald-200 bg-emerald-50 px-4 py-3.5 text-sm text-emerald-900" data-id="element-69" data-mp="1ozafio0qn34o">
              <ArrowUpRightIcon className="mt-0.5 h-5 w-5 shrink-0" aria-hidden="true" data-id="element-70" data-mp="23mn8uywsed5m" />
              <p data-id="element-71" data-mp="1yotxzskjguz0">
                <strong className="font-semibold" data-id="element-72" data-mp="1yqesxjarmfxe">
                  This loan moves {officer.name.split(' ')[0]} from {tierBefore.name} to{' '}
                  {landingTier.name}
                </strong>{' '}
                — every loan from here pays {formatBps(landingTier.basisPoints)} with a{' '}
                {formatPercent(landingTier.overageSplit)} overage split.
              </p>
            </div> : nextTier && nextRow ? <div className="rounded-xl border border-slate-200 bg-slate-50 px-4 py-3.5 text-sm text-slate-700" data-id="element-73" data-mp="2wbatqtdmj0in">
              <p data-id="element-74" data-mp="2rc7op8xn8uao">
                <strong className="font-semibold text-slate-900" data-id="element-75" data-mp="2rcpvgzhxmqwu">
                  {Math.max(0, nextTier.minUnits - unitsAfter)} more loan
                  {Math.max(0, nextTier.minUnits - unitsAfter) === 1 ? '' : 's'} to reach{' '}
                  {nextTier.name}
                </strong>{' '}
                — the same deal would pay {formatCurrency(nextRow.total)} there (
                {formatCurrency(nextRow.total - landingRow.total)} more).
              </p>
            </div> : <div className="rounded-xl border border-slate-200 bg-slate-50 px-4 py-3.5 text-sm text-slate-700" data-id="element-76" data-mp="3hnruqea7nupe">
              <p data-id="element-77" data-mp="3mnxj9ll6p139">Top tier reached — this deal already pays at the maximum rate.</p>
            </div>}

          {sellPrice < PAR_PRICE ? <div className="flex items-start gap-3 rounded-xl border border-amber-200 bg-amber-50 px-4 py-3.5 text-sm text-amber-900" data-id="element-78" data-mp="3cv89qw971xak">
              <TriangleAlertIcon className="mt-0.5 h-5 w-5 shrink-0" aria-hidden="true" data-id="element-79" data-mp="4edf242jvj7k" />
              <p data-id="element-80" data-mp="98hnkrrgrzar">
                Selling below par creates a {formatCurrency(Math.abs(overageDollars))} concession.
                The LO absorbs {formatPercent(landingTier.overageSplit)} of it.
              </p>
            </div> : null}

          <div className="overflow-hidden rounded-xl border border-slate-200" data-id="element-81" data-mp="36vlp8wyqsoje">
            <TierLadderTable rows={rows} data-id="element-82" data-mp="31x22wvg8laqi" />
          </div>
        </div>
      </div>
    </Card>;
}